import { tokenManager } from "lib/tokenManager";
import { trpc } from "lib/trpc";
import * as React from "react";
import { isRefreshDue } from "@compass/shared/utils/sessionRefresh";

/**
 * PRTL-1356: tokens expire after 7 days, so re-mint once the current one is
 * a day old (sliding window). Checked on mount and whenever the window
 * regains focus; the server revokes the replaced session as part of the
 * refresh.
 *
 * PRTL-1519: "a day old" is measured on this device's clock from when the
 * token arrived (`isRefreshDue`), and attempts are debounced, so a device
 * clock running days fast no longer re-mints in a loop.
 */
let lastAttemptAt: number | null = null;

export const SessionRefresh = () => {
    const token = React.useSyncExternalStore(tokenManager.subscribe, tokenManager.get);
    const refreshMutation = trpc.auth.refreshToken.useMutation({
        onSuccess: ({ token: newToken }) => tokenManager.set(newToken),
    });
    const inFlight = React.useRef(false);

    React.useEffect(() => {
        if (!token) return;

        const maybeRefresh = () => {
            // Masquerade sessions cannot be extended (server rejects too).
            if (inFlight.current || tokenManager.isMasquerading()) return;
            const now = Date.now();
            const clock = { now, receivedAt: tokenManager.receivedAt(token), lastAttemptAt };
            if (!isRefreshDue(token, clock)) return;
            lastAttemptAt = now;
            inFlight.current = true;
            // Tracked so a request that meets the revoked old token waits for the new one
            // and retries, instead of logging out (App.tsx fetch).
            tokenManager
                .trackRefresh(refreshMutation.mutateAsync())
                .catch(() => undefined)
                .finally(() => {
                    inFlight.current = false;
                });
        };

        maybeRefresh();
        window.addEventListener("focus", maybeRefresh);
        return () => window.removeEventListener("focus", maybeRefresh);
        // refreshMutation identity changes every render; token is the real dependency.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    return null;
};
